import { VendorRiskSectionEnum } from '@/modules/agent/types/vendorAssessment';
import { VendorCompanyDataItem } from '@/modules/agent/types/vendorCompanyData';
import { handleAutoSaveOfVendorAssessmentResponse } from '@/modules/agent/use-cases/vendor.use-case';
import parsePromise from '@/shared/utils/parsePromise';
import { useCallback, useEffect, useRef } from 'react';
import { Params, useParams } from 'react-router-dom';
import {
  processFileForVendorRiskReview,
  processVendorRiskReviewData,
} from '../../../use-cases/vendor-risk.use-case';
import { useVendorRiskAssessmentStore } from './useVendorRiskAssessmentStore';


export type VendorPageType =
  | 'OVERVIEW'
  | 'COMPANY'
  | 'SUPPLY_CHAIN'
  | 'DETAILED_REVIEW'
  | 'POTENTIAL_ISSUES';

export const useVendorRiskAssessment = () => {
  const { id } = useParams<keyof Params>() as Params;
  const savingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    // states
    selectedTab,
    combinedVendorRiskData,
    vendorOverview,
    vendorCompanyData,
    vendorSupplyChainData,
    vendorAssessmentData,
    vendorPotentialIssuesData,
    changesMade,
    // functions and dispatchers
    setSelectedTab,
    setCombinedVendorRiskData,
    setVendorOverview,
    setVendorCompanyData,
    setVendorSupplyChain,
    setVendorAssessment,
    setVendorPotentialIssues,
    setIsSaving,
  } = useVendorRiskAssessmentStore();

  const handleTabChange = useCallback(
    (newTab: VendorPageType) => {
      setSelectedTab(newTab);
    },
    [setSelectedTab]
  );

  useEffect(() => {
    if (changesMade > 0 && id) {
      const t = setTimeout(() => {
        setIsSaving(true);
        handleAutoSaveOfVendorAssessmentResponse({ agentId: id })
          .then((res) => {
            // Clear existing timeout to implement debouncing for setIsSaving(false)
            if (savingTimeoutRef.current) {
              clearTimeout(savingTimeoutRef.current);
            }
            savingTimeoutRef.current = setTimeout(() => {
              setIsSaving(false);
            }, 2000);
          })
          .catch((err) => {
            console.error('Auto-save failed:', err);
            // Reset saving state immediately on error
            setIsSaving(false);
            // TODO: Add user notification for save failures
            // addNotification({
            //   message: 'Failed to auto-save changes. Please try again.',
            //   type: 'error'
            // });
          });
      }, 1000);
      return () => {
        clearTimeout(t);
      };
    }
  }, [changesMade, id, setIsSaving]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
        savingTimeoutRef.current = null;
      }
    };
  }, []);

  const loadVendorRiskData = useCallback(async () => {
    if (id) {
      const [error, res] = await parsePromise(
        processFileForVendorRiskReview(id)
      );

      if (error) {
        console.error('Failed to load vendor risk data:', error);
        // TODO: Add user notification for loading failures
        // addNotification({
        //   message: 'Failed to load vendor assessment data. Please refresh the page.',
        //   type: 'error'
        // });
        return;
      }

      setCombinedVendorRiskData(res);
      if (res[VendorRiskSectionEnum.vendor_overview]) {
        setVendorOverview(res[VendorRiskSectionEnum.vendor_overview], true);
      }
      if (res[VendorRiskSectionEnum.company_profile]) {
        setVendorCompanyData(
          res[
          VendorRiskSectionEnum.company_profile
          ] as unknown as VendorCompanyDataItem[],
          true
        );
      }
      if (res[VendorRiskSectionEnum.vendor_supply_chain]) {
        setVendorSupplyChain(
          res[VendorRiskSectionEnum.vendor_supply_chain],
          true,
        );
      }
      if (res[VendorRiskSectionEnum.potential_issues]) {
        setVendorPotentialIssues(
          res[VendorRiskSectionEnum.potential_issues],
          true,
        );
      }
      if (
        res[VendorRiskSectionEnum.zania_security_assessment] ||
        res[VendorRiskSectionEnum.ai_security_assessment]
      ) {
        const vendorRiskReviewData = [
          ...res[VendorRiskSectionEnum.zania_security_assessment],
          ...res[VendorRiskSectionEnum.ai_security_assessment],
        ];

        setVendorAssessment(vendorRiskReviewData, true);

        processVendorRiskReviewData(id, vendorRiskReviewData);
      }
    }
  }, [
    id,
    setCombinedVendorRiskData,
    setVendorOverview,
    setVendorSupplyChain,
    setVendorPotentialIssues,
    setVendorAssessment,
    setVendorCompanyData,
  ]);

  return {
    // states
    selectedTab,
    combinedVendorRiskData,
    vendorOverview,
    vendorCompanyData,
    vendorSupplyChainData,
    vendorAssessmentData,
    vendorPotentialIssuesData,
    // functions and dispatchers
    handleTabChange,
    loadVendorRiskData,
  };
};
